import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import TableTop from "./../../../component/atoms/layout/table/tableTop";
import TableCount from "./../../../component/molecules/tableCount";
import SearchBar from "./../../../component/atoms/input/searchBar";
import ExcelBtn from "./../../../component/atoms/button/excelBtn";
import { route } from "../../../router/route";
import Pagination from "./../../../component/molecules/pagination";
import Table from "../../../component/atoms/layout/table/table";
import Tr from "./../../../component/atoms/layout/table/tr";
import Th from "./../../../component/atoms/layout/table/th";
import Checkbox from "./../../../component/atoms/input/checkbox";
import TableText from "./../../../component/atoms/text/table/tableText";
import Td from "./../../../component/atoms/layout/table/td";
import DetailBtn from "./../../../component/atoms/button/detailBtn";
import SubSideBar from "../../../component/organisms/subSideBar";
import SubSideBarItem from "../../../component/molecules/subSideBarItem";
import PretendardText from "../../../component/atoms/text/pretendardText";
import GrayBtn from "../../../component/atoms/button/grayBtn";
import Popup from "../../../component/molecules/popup";
import PopupOverLay from "../../../component/templates/PopupOverlay";
import MainInput from "./../../../component/atoms/input/mainInput";
import RedBtn from "./../../../component/atoms/button/redBtn";
import EventBtn from "../../../component/atoms/button/eventBtn";
import { toastMsg } from "../../../util/toastMsg";
import { errorHandler } from "../../../util/errorHandler";
import Modal from "../../../component/templates/Modal";

import GroupApi from "../../../api/group.api";
import MemberApi from "../../../api/member.api";
import Empty from "../../../component/molecules/empty";

import AdminGroupManagePopup from "../../../component/templates/user/userAdmin/adminGroupManagePopup";
import SelectBoxValue from "../../../component/organisms/selectBoxValue";
import { cMembershipList } from "../../../constants/userInfoConstants";
import { formatNumberWithCommas } from "../../../util/formatNumber";

// sessionStorage 초기화
function sessionStorageInit() {
  sessionStorage.removeItem("sCheckedListStorage");
  const event = new Event("sessionStorageChange");
  window.dispatchEvent(event);
}

const UserAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars
  const [isWriteAuthority, setIsWriteAuthority] = useState(false);

  // ---------- 쓰기 권한체크 ---------- //
  useEffect(() => {
    const writeMenu = sessionStorage.getItem("writeMenu");

    if (writeMenu?.includes(route.user_admin)) {
      setIsWriteAuthority(true);
    } else {
      setIsWriteAuthority(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [keyword, setKeyword] = useState("");

  const page = useMemo(() => {
    return searchParams.get("page") ? searchParams.get("page") : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 10, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  const [groupPopupOpen, setGroupPopupOpen] = useState(false);
  const [invitePopupOpen, setInvitePopupOpen] = useState(false);

  const [selectedGroupValue, setSelectedGroupValue] = useState();
  const [selectedGroupId, setSelectedGroupId] = useState(0);
  const [operateGroupList, setOperateGroupList] = useState([]);

  const [selectedGroupInviteList, setSelectedGroupInviteList] = useState([]);
  const [selectedGroupInvite, setSelectedGroupInvite] = useState("");
  const [selectedGroupInviteBoxOpen, setSelectedGroupInviteBoxOpen] =
    useState(false);

  // ---------- 데이터 관련 ---------- //
  const [adminList, setAdminList] = useState([]);

  useEffect(() => {
    getOperateGroupList();

    return () => {
      sessionStorage.removeItem("sCheckedListStorage");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    setSelectedGroupId(
      searchParams.get("groupId") ? Number(searchParams.get("groupId")) : 0
    );
    setKeyword(searchParams.get("keyword") ? searchParams.get("keyword") : "");

    getAdminList();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // 운영진 그룹 가져오기
  const getOperateGroupList = async () => {
    try {
      const data = {
        page: 1,
        size: 100,
        memberGroupType: "OPERATE",
      };

      const response = (await GroupApi.getList(data)).data.data.content;
      setOperateGroupList(response);

      let temp = response.map((item) => {
        return {
          value: item.id,
          title: item.title,
        };
      });

      setSelectedGroupInviteList(temp);
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (selectedGroupValue) {
      setGroupPopupOpen(true);
    }
  }, [selectedGroupValue]);

  const getAdminList = async () => {
    try {
      const data = {
        page: Number(page),
        size: pagination.size,
        role: 0,
      };

      if (searchParams.get("groupId")) {
        data.groupId = Number(searchParams.get("groupId"));
      }

      if (searchParams.get("keyword")) {
        data.keyword = searchParams.get("keyword");
      }

      const response = (await MemberApi.getList(data)).data.data;

      const processedResponse = [...response.content].map((item) => {
        if (item.age === null) {
          item.age = "-";
        } else {
          const year = item.age.split("-")[0];
          const currentYear = new Date().getFullYear();
          const koreanAge = currentYear - year + 1;

          if (koreanAge < 10) {
            item.age = "10대 미만";
          } else if (koreanAge < 20) {
            item.age = "10대";
          } else if (koreanAge < 30) {
            item.age = "20대";
          } else if (koreanAge < 40) {
            item.age = "30대";
          } else if (koreanAge < 50) {
            item.age = "40대";
          } else if (koreanAge < 60) {
            item.age = "50대";
          } else if (koreanAge >= 60) {
            item.age = "60대 이상";
          }
        }

        return {
          ...item,
          checked: false,
        };
      });

      setAdminList(processedResponse);
      setPagination({
        ...pagination,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / pagination.size),
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  const sessionStorageCheckedList = sessionStorage.getItem(
    "sCheckedListStorage"
  );
  const [checkedListStorage, setCheckedListStorage] = useState([]);
  const [buttonGroupOpen, setButtonGroupOpen] = useState(false);
  const [isAllCheck, setIsAllCheck] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setCheckedListStorage(
        sessionStorageCheckedList ? JSON.parse(sessionStorageCheckedList) : []
      );
    };

    window.addEventListener("sessionStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("sessionStorageChange", handleStorageChange);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    sessionStorage.setItem(
      "sCheckedListStorage",
      JSON.stringify(checkedListStorage)
    );
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (adminList.length > 0) {
      const idsInArray1 = new Set(checkedListStorage.map((item) => item.id));

      const allIdsIncluded = adminList.every((item) =>
        idsInArray1.has(item.id)
      );

      setIsAllCheck(allIdsIncluded);
    }

    if (checkedListStorage.length > 0) {
      setButtonGroupOpen(true);
    } else {
      setButtonGroupOpen(false);
    }
  }, [checkedListStorage, adminList]); /// eslint-disable-line react-hooks/exhaustive-deps

  //삭제관련
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const deleteFunc = async () => {
    try {
      let memberIdsTemp = checkedListStorage.map((item) => item.id);

      let data = {
        memberIds: memberIdsTemp,
      };
      await MemberApi.managerDelete(data);

      toast(toastMsg.delete);
      setDeleteModalOpen(false);
      getAdminList();
      sessionStorageInit();
      navigate(
        `${route.user_admin}?page=1&groupId=${selectedGroupId}&keyword=${keyword}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  const [selectedGroupIdList, setSelectedGroupIdList] = useState([]);
  const [buttonGroupFilterOpen, setButtonGroupFilterOpen] = useState(false);

  const createGroupFunc = async () => {
    try {
      let memberIdsTemp = checkedListStorage.map((item) => item.id);

      const data = {
        memberIds: memberIdsTemp,
        groupIds: selectedGroupIdList,
      };

      await MemberApi.adminCreateGroup(data);

      getAdminList();
      toast(toastMsg.create);
      setButtonGroupOpen(false);
      sessionStorageInit();
      setSelectedGroupIdList([]);

      navigate(
        `${route.user_admin}?page=1&groupId=${selectedGroupId}&keyword=${keyword}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (checkedListStorage && checkedListStorage.length === 1) {
      if (
        checkedListStorage[0]?.groupList &&
        checkedListStorage[0]?.groupList.length > 0
      ) {
        let temp = checkedListStorage[0]?.groupList.map((group) => group.id);

        setSelectedGroupIdList(temp);
      }
    } else if (
      Number(selectedGroupId) !== 0 &&
      Number(selectedGroupId) !== -1
    ) {
      setSelectedGroupIdList([Number(selectedGroupId)]);
    } else {
      setSelectedGroupIdList([]);
    }
  }, [checkedListStorage, selectedGroupId]);

  const [inviteEmail, setInviteEmail] = useState("");

  const inviteFunc = async () => {
    try {
      if (selectedGroupInvite === "") {
        toast("그룹을 선택해주세요.");
        return;
      }

      if (inviteEmail === "") {
        toast("초대 계정을 입력력해주세요.");
        return;
      }

      let data = {
        groupId: Number(selectedGroupInvite),
        email: inviteEmail,
      };

      await MemberApi.managerInvite(data);

      toast("운영진이 초대되었습니다.");
      setSelectedGroupInvite("");
      setInviteEmail("");

      setInvitePopupOpen(false);
      sessionStorageInit();
    } catch (error) {
      if (error.response?.status === 400) {
        toast("초대할 수 없는 계정입니다.");
        return;
      }

      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 사이드 바 영역 */}
        <SubSideBar title="운영진 그룹">
          <SubSideBarItem
            selected={selectedGroupId === 0 ? true : false}
            onClick={() => {
              if (selectedGroupId === 0) return;

              sessionStorageInit();
              navigate(`${route.user_admin}?page=1&groupId=0&keyword=`);
            }}
          >
            전체
          </SubSideBarItem>
          {operateGroupList.length > 0 && (
            <span
              style={{
                width: "100%",
                height: 0.5,
                background: "rgba(255, 255, 255, 0.12)",
              }}
            />
          )}

          {operateGroupList.length > 0
            ? operateGroupList.map((item, index) => {
                return (
                  <SubSideBarItem
                    selected={selectedGroupId === item.id ? true : false}
                    key={index}
                    icon="setting"
                    onClick={() => {
                      if (selectedGroupId === item.id) return;

                      sessionStorageInit();
                      navigate(
                        `${route.user_admin}?page=1&groupId=${item.id}&keyword=`
                      );
                    }}
                    settingHandler={() => {
                      if (isWriteAuthority) {
                        setSelectedGroupValue(item);
                      } else {
                        toast("권한이 없습니다.");
                      }
                    }}
                  >
                    {item.title}
                  </SubSideBarItem>
                );
              })
            : null}
          <SubSideBarFooter>
            <SubSideBarFooterText
              onClick={() => {
                navigate(route.user_admin_invite);
              }}
            >
              운영진 초대 내역
            </SubSideBarFooterText>
          </SubSideBarFooter>
          {isWriteAuthority && (
            <SubSideBarFooter>
              <SubSideBarFooterText
                onClick={() => {
                  setSelectedGroupValue({
                    id: 0,
                  });
                }}
              >
                새 그룹 만들기
              </SubSideBarFooterText>
            </SubSideBarFooter>
          )}
        </SubSideBar>

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>운영진 설정</TableTitle>
          {/* 테이블 상단 영역 */}
          <TableTop>
            <TableCount text={"회원"} count={pagination.totalCount} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <SearchBar
                value={keyword}
                placeholder={"이름, 아이디(이메일), 닉네임"}
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    sessionStorageInit();
                    navigate(
                      `${route.user_admin}?page=1&groupId=${selectedGroupId}&keyword=${keyword}`
                    );
                  }
                }}
                onClick={() => {
                  sessionStorageInit();
                  navigate(
                    `${route.user_admin}?page=1&groupId=${selectedGroupId}&keyword=${keyword}`
                  );
                }}
              />
              {/* <ExcelBtn /> */}
              {isWriteAuthority && (
                <EventBtn
                  onClick={() => {
                    setInvitePopupOpen(true);
                  }}
                  text={"운영진 초대하기"}
                />
              )}
            </div>
          </TableTop>

          {/* 버튼 그룹 */}
          {buttonGroupOpen && isWriteAuthority && (
            <div
              style={{
                width: "100%",
                height: 49,
                display: "flex",
                gap: 12,
              }}
            >
              {/* 버튼 그룹 - 그룹지정 */}
              <div
                style={{
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <GrayBtn
                  onClick={() => {
                    setButtonGroupFilterOpen(!buttonGroupFilterOpen);
                  }}
                >
                  그룹지정
                </GrayBtn>
                {buttonGroupFilterOpen && (
                  <FilterWrapper
                    style={{
                      width: 230,
                      top: 36,
                      padding: 16,
                    }}
                  >
                    {operateGroupList.length > 0
                      ? operateGroupList.map((item, index) => {
                          return (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: 10,
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                if (
                                  selectedGroupIdList.some(
                                    (item02) => item02 === item.id
                                  )
                                ) {
                                  let groupIdListTemp =
                                    selectedGroupIdList.filter(
                                      (item02) => item02 !== item.id
                                    );

                                  setSelectedGroupIdList(groupIdListTemp);
                                } else {
                                  setSelectedGroupIdList([item.id]);
                                }
                              }}
                            >
                              <PretendardText
                                style={{
                                  fontWeight: "500",
                                  fontSize: "13px",
                                  lineHeight: "21.5px",
                                  color: selectedGroupIdList.some(
                                    (item02) => item02 === item.id
                                  )
                                    ? "rgba(181, 181, 181, 0.5)"
                                    : "#aaa",
                                }}
                              >
                                {item.title}
                              </PretendardText>
                              {selectedGroupIdList.some(
                                (item02) => item02 === item.id
                              ) && (
                                <img
                                  src="/assets/admin/icons/ico_select_checked.svg"
                                  alt="선택됨"
                                  style={{
                                    width: 18,
                                    height: 18,
                                  }}
                                />
                              )}
                            </div>
                          );
                        })
                      : null}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: 10,
                        borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                      }}
                    >
                      <PretendardText
                        onClick={() => {
                          setSelectedGroupValue({
                            id: 0,
                          });
                        }}
                        style={{
                          fontWeight: "500",
                          fontSize: "13px",
                          lineHeight: "21.5px",
                          color: "rgba(181, 181, 181, 0.5)",
                          cursor: "pointer",
                        }}
                      >
                        새 그룹 만들기
                      </PretendardText>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                      }}
                    >
                      <GrayBtn
                        style={{}}
                        onClick={() => {
                          createGroupFunc();

                          setButtonGroupFilterOpen(false);
                        }}
                      >
                        적용
                      </GrayBtn>
                    </div>
                  </FilterWrapper>
                )}
              </div>

              <RedBtn
                style={{
                  backgroundColor: "#272829",
                }}
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
              >
                운영진 삭제
              </RedBtn>
            </div>
          )}

          {/* 테이블 영역 */}
          <Table>
            <colgroup>
              {/* 합계: 1044px */}
              {/* 체크박스 */}
              <col width="61px" />
              {/* 이름/아이디(이메일) */}
              <col width="224px" />
              {/* 연령/성별 */}
              <col width="92px" />
              {/* 그룹 */}
              <col width="122px" />
              {/* 회원등급/레벨 */}
              <col width="115px" />
              {/* 누적 구매 */}
              <col width="136px" />
              {/* 보유 포인트 */}
              <col width="195px" />
              {/* 보기 */}
              <col width="99px" />
            </colgroup>
            <thead>
              <Tr style={{ height: "41px", background: "#272829" }}>
                <Th style={{ paddingLeft: "20px" }}>
                  <Checkbox
                    checked={isAllCheck}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // 배열 병합 및 중복 제거
                        const mergedArray = [
                          ...adminList,
                          ...checkedListStorage,
                        ].reduce((acc, current) => {
                          const existingItem = acc.find(
                            (item) => item.id === current.id
                          );
                          if (!existingItem) {
                            acc.push(current);
                          }
                          return acc;
                        }, []);

                        setCheckedListStorage(mergedArray);
                      } else {
                        const idsToExclude = new Set(
                          adminList.map((item) => item.id)
                        );
                        const filteredArray = checkedListStorage.filter(
                          (item) => !idsToExclude.has(item.id)
                        );

                        setCheckedListStorage(filteredArray);
                      }
                    }}
                  />
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    이름/아이디(이메일)
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    연령/성별
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    그룹
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    회원등급/레벨
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                      textAlign: "right",
                    }}
                  >
                    누적 구매
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                      textAlign: "right",
                      paddingRight: "56px",
                    }}
                  >
                    보유 포인트
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    보기
                  </TableText>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {adminList.map((item, index) => {
                return (
                  <Tr
                    key={index}
                    style={{
                      height: "59px",
                      padding: "20px 0px",
                      background: "transparent",
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <Td style={{ paddingLeft: "20px" }}>
                      <Checkbox
                        checked={checkedListStorage.some(
                          (item02) => item02.id === item.id
                        )}
                        onChange={() => {
                          if (
                            checkedListStorage.length > 0 &&
                            checkedListStorage.some(
                              (item02) => item02.id === item.id
                            )
                          ) {
                            let temp = checkedListStorage.filter(
                              (item02) => item02.id !== item.id
                            );
                            setCheckedListStorage(temp);
                          } else {
                            setCheckedListStorage([
                              item,
                              ...checkedListStorage,
                            ]);
                          }
                        }}
                      />
                    </Td>
                    <Td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <TableText
                          style={{
                            maxWidth: 166,
                          }}
                        >
                          {item.name}
                        </TableText>
                        <TableText
                          style={{
                            maxWidth: 166,
                          }}
                        >
                          {item.email}
                        </TableText>
                      </div>
                    </Td>
                    <Td>
                      <TableText>
                        {item.age}/{item.gender === "male" ? "남" : "여"}
                      </TableText>
                    </Td>
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "94px",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          wordBreak: "normal",
                          whiteSpace: "normal",
                          WebkitLineClamp: 2, // 2줄부터 ... 표시
                        }}
                      >
                        {item?.groupList && item?.groupList?.length > 0
                          ? item.groupList.map((group, index02) => {
                              return (
                                <span key={index02}>
                                  {group.title}
                                  {index02 !== item.groupList.length - 1 &&
                                    ", "}
                                </span>
                              );
                            })
                          : "그룹 없음"}
                      </TableText>
                    </Td>
                    <Td>
                      <TableText>
                        {cMembershipList[item.membership]}/Lv.
                        {item.writingLevel}
                      </TableText>
                    </Td>
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "136px",
                          textAlign: "right",
                        }}
                      >
                        {item.purchaseCount}회/
                        {formatNumberWithCommas(item.purchaseAmount)}원
                      </TableText>
                    </Td>
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "99px",
                          marginLeft: 40,
                          textAlign: "right",
                        }}
                      >
                        {Number(item.totalPoint).toLocaleString()}P
                      </TableText>
                    </Td>
                    <Td>
                      <DetailBtn
                        onClick={() => {
                          navigate(`${route.user_admin_detail}/${item.id}`);
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {adminList.length > 0 ? null : <Empty />}

          {/* 페이지네이션 영역 */}
          <Pagination
            route={route.user_admin}
            queryString={`groupId=${selectedGroupId}&keyword=${keyword}`}
            currentPage={page}
            totalPages={pagination.totalPages}
            rangeSize={pagination.rangeSize}
            first={pagination.first}
            last={pagination.last}
          />
        </DetailPageLayout>
      </MainLayout>

      {/* 삭제 모달 */}
      {deleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setDeleteModalOpen}
          onClick={deleteFunc}
        />
      )}

      {/* 그룹 정보 수정 팝업 */}
      {groupPopupOpen && (
        <AdminGroupManagePopup
          setPopupOpen={setGroupPopupOpen}
          selectedGroupValue={selectedGroupValue}
          getOperateGroupList={getOperateGroupList}
        />
      )}

      {/* 운영진 초대하기 팝업 */}
      {invitePopupOpen && (
        <PopupOverLay
          isOpen={true}
          onClick={() => {
            setInvitePopupOpen(false);
          }}
        >
          <Popup
            headerTitle="운영진 초대하기"
            footerBtnText="저장하기"
            onClick={() => {
              setInvitePopupOpen(false);
            }}
            footerBtnHandler={() => {
              inviteFunc();
            }}
          >
            <div
              style={{
                width: "100%",
                height: 260,
                padding: "0px 30px",
                margin: "24px 0px 20px 0px",
                display: "flex",
                flexDirection: "column",
                gap: 30,
              }}
            >
              {/* 그룹명 */}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 38,
                }}
              >
                <PretendardText
                  style={{
                    width: 84,
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "22.75px",
                    color: "#aaa",
                  }}
                >
                  초대 계정 <br />
                  아이디(이메일)
                </PretendardText>
                <MainInput
                  type={"text"}
                  placeholder={"아이디(이메일)을 입력해 주세요."}
                  style={{ width: "338px" }}
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                  }}
                />
              </div>
              {/* 그룹 유형 선택 */}
              <div
                style={{
                  width: "100%",
                  display: "flex",

                  gap: 30,
                }}
              >
                <PretendardText
                  style={{
                    marginTop: 12.5,
                    width: 92,
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "22.75px",
                    color: "#aaa",
                  }}
                >
                  운영진 그룹 설정
                </PretendardText>
                <div
                  style={{
                    width: "fit-content",
                    display: "flex",
                    flexDirection: "column",
                    gap: 9,
                  }}
                >
                  <SelectBoxValue
                    placeholder={"운영 그룹을 선택해주세요."}
                    selectedValue={selectedGroupInvite}
                    isOpen={selectedGroupInviteBoxOpen}
                    wrapperHandler={() => {
                      setSelectedGroupInviteBoxOpen(
                        !selectedGroupInviteBoxOpen
                      );
                    }}
                    itemList={selectedGroupInviteList}
                    selectedValueSetterFunc={(value) => {
                      setSelectedGroupInvite(value);
                    }}
                  />
                  <PretendardText
                    style={{
                      fontSize: "13px",
                      fontWeight: "500",
                      lineHeight: "18.75px",
                      color: "rgba(181, 181, 181, 0.5)",
                    }}
                  >
                    운영 그룹에 설정되어 있는 권한 정보가 적용됩니다
                  </PretendardText>
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <PretendardText
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    lineHeight: "18.75px",
                    color: "#cf6679",
                  }}
                >
                  · 운영진 그룹으로 설정된 사용자는 (관리자 페이지) 주소를
                  브라우저에 직접 입력하여 관리자 페이지에 접근할 수 있습니다.
                </PretendardText>
                <PretendardText
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    lineHeight: "18.75px",
                    color: "#cf6679",
                  }}
                >
                  · 초대된 운영진 그룹은 라이팅크루 가입 시 설정한
                  아이디(이메일)와 비밀번호로 admin 홈페이지에 접속할 수
                  있습니다. [회원정보관리에서 확인 가능]
                </PretendardText>
              </div>
            </div>
          </Popup>
        </PopupOverLay>
      )}
    </>
  );
};

export default UserAdmin;

const SubSideBarFooter = styled.div.attrs((props) => {})`
  width: 100%;
  height: 38px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0px 16px;
  border-top: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const SubSideBarFooterText = styled(PretendardText).attrs((props) => {})`
  font-weight: 500;
  font-size: 13px;
  line-height: 18.75px;
  color: #aaa;
  cursor: pointer;
`;

const FilterWrapper = styled.div.attrs((props) => {})`
  padding: 10px;
  border-radius: 3px;
  background: #272829;
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 48px;
  z-index: 5;
`;
